import { Link } from 'react-router-dom';
import { formatCurrency } from '@/utils/formatUtils';
import { getProductThumbnailUrl } from '@/utils/imageUtils';
import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import RarityBadge from '@/components/RarityBadge';
import { trackEvent } from '@/utils/googleAnalytics';

const CardItemView = ({
  id,
  type,
  image,
  brand,
  name,
  price,
  orders,
  condition,
  sold = false,
  className,
  isListing = false,
  customRightText = null,
  trackEventName = null,
  code,
  rawCode,
  set,
  rarityInfo,
  linkTo = null,
}) => {
  // Generate mock price and orders if not provided by API
  const displayPrice = price || 0;
  const displayOrders = orders || 0;
  
  // Animation related states and refs
  const cardRef = useRef(null);
  const [isHovered, setIsHovered] = useState(false);
  const [rotation, setRotation] = useState({ x: 0, y: 0 });
  const [shinePosition, setShinePosition] = useState({ x: 0, y: 0 });

  // Handle mouse movement for animation
  const handleMouseMove = (e) => {
    if (!cardRef.current || type !== 'PSA_SINGLE_CARD') return;
    
    const card = cardRef.current;
    const { left, top, width, height } = card.getBoundingClientRect();
    
    const xPos = (e.clientX - left - width / 2) / 10;
    const yPos = (e.clientY - top - height / 2) / 10;
    
    setRotation({
      x: yPos,
      y: xPos
    });
    
    // Set shine position
    setShinePosition({
      x: e.clientX - left,
      y: e.clientY - top
    });
  };

  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    // Reset rotations smoothly
    setRotation({ x: 0, y: 0 });
  };

  const { t } = useTranslation();
  const displayImage = getProductThumbnailUrl(image);

  const handleCardClick = () => {
    if (trackEventName) {
      trackEvent(trackEventName, {
        product_id: id,
        product_name: name,
      });
    }
  };

  return (
    <Link
      to={linkTo || (isListing ? `/listing/${id}` : `/card/${id}`)}
      className={`aspect-[0.723] px-1.5 pt-1.5 pb-3 bg-white rounded-md border border-[#dddddd] flex-col justify-start items-start gap-2 inline-flex hover:border-[#111111] overflow-hidden transition-shadow duration-300 ${isHovered ? 'shadow-lg' : ''} ${className}`}
      ref={cardRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onMouseMove={handleMouseMove}
      onClick={handleCardClick}
    >
      <div className="aspect-[1] w-full relative bg-[#FFFFFF] rounded overflow-hidden">
        {sold && (
          <div className="absolute inset-0 mix-blend-darken bg-black/20" />
        )}
        <div className="h-full flex justify-center items-center">
          <div className='overflow-hidden relative flex justify-center items-center w-full h-full'>
            <img 
              className="h-full object-contain mix-blend-darken"
              src={displayImage} 
              alt={name}
              style={{
                transition: isHovered ? 'none' : 'transform 0.3s ease-out',
                transform: type !== 'PSA_SINGLE_CARD' ? 'none' : `perspective(1000px) rotateX(${rotation.x}deg) rotateY(${rotation.y}deg)`,
                transformStyle: 'preserve-3d',
                display: 'block',
              }}
            />
            
            {/* Shine effect */}
            {isHovered && type === 'PSA_SINGLE_CARD' && (
              <div
                className="absolute top-[-100%] left-[-100%] pointer-events-none"
                style={{
                  background: 'linear-gradient(55deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.4) 40%, rgba(255, 255, 255, 0.85) 50%, rgba(255, 255, 255, 0.4) 60%, rgba(255,255,255,0) 100%)',
                  opacity: 0.7,
                  transform: `translate(${shinePosition.x - 100}px, ${shinePosition.y - 100}px)`,
                  width: '300%',
                  height: '300%',
                }}
              />
            )}
          </div>
        </div>
        
        {!isHovered && (
          <div className="bottom-[4px] absolute w-full px-1 flex">
            <div className='px-1.5 py-[3px] bg-[#EEEEEEBF] backdrop-blur-[16px] rounded font-["Noto_Sans_JP"] font-normal text-xs leading-[150%] text-[#111111] line-clamp-1'>{condition || brand}</div>
          </div>
        )}
        {sold && (
          <div className="w-[55px] h-[55px] left-0 top-0 absolute">
            <div className="absolute inset-0">
              <svg width="55" height="55" viewBox="0 0 55 55" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M55 0H0V55L55 0Z" fill="#FF5601"/>
              </svg>
            </div>
            <div className="left-0 top-[23.33px] absolute origin-top-left -rotate-45 text-white text-xs font-bold">
              SOLD
            </div>
          </div>
        )}
      </div>
      <div className="self-stretch px-1.5 flex-col justify-end items-start flex flex-1">
        <div className="self-stretch text-[#111111] text-sm line-clamp-1">
          {name}
          {type !== 'CARD_BOX' && rarityInfo?.sub?.name ? `(${rarityInfo.sub.name})` : ''}
        </div>
        <div className="flex flex-wrap items-center gap-1 mt-1">
          {type !== 'CARD_BOX' && rarityInfo?.main && (
            <RarityBadge rarity={rarityInfo.main} />
          )}
          <div className='h-[18px]'>
            {(set?.code || (rawCode && type !== 'CARD_BOX')) && (
              <div className="h-[18px] px-1 bg-[#f4f4f4] rounded-[3px] outline outline-[0.50px] outline-offset-[-0.50px] outline-[#dddddd] flex justify-start items-center gap-2.5">
                <div className="justify-start text-[#111111] text-sm font-normal leading-[14px]">
                  {type === 'CARD_BOX' ? set?.code : (set?.code && rawCode ? `${set.code} ${rawCode}` : set?.code || rawCode)}
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="self-stretch flex items-center gap-2 mt-2">
          <div className={`text-lg font-medium ${displayPrice === 0 ? 'text-[#aaaaaa]' : 'text-[#111111]'}`}>
            {displayPrice === 0 ? t('common.sold_out') : formatCurrency(displayPrice)}
          </div>
          <div className="text-[#aaaaaa] text-sm flex-1 flex justify-end">
            <div className='max-w-full line-clamp-1'>
              {customRightText !== null ? customRightText : !!displayOrders ? `${displayOrders}+ ${t('card.orders')}` : ''}
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
};

export default CardItemView; 
